import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AuthAction, TranslateAction } from '../../actions';
import { Sitemap } from '../../config';
import { RayForm, RayFormValidation } from '../../components/common';
import { EditSaveCancelBar } from '../../components/common/Buttons';
import { Translate as t } from '../../config/Translate';
import { loadLang } from '../../config/langResources';
import { LANG_SELECT_OPTIONS } from '../../lib/preLoginLang';
import { resolveUserLangCode, resolveStoredMailLangCode } from '../../config/currentLang';
import { userFieldLabel } from '../admin/userFormFields';
import { isAdminPathname } from '../../lib/isAdminPathname';
import { isValidWholeDays } from '../../lib/dashboardSetting';
import { getLicenseRole } from '../../lib/licenseHelper';
import { ROLE_EXTERNAL_DEALER } from '../../config/appConstants';

// BUG-221: 여기서 'moment/locale/*' 를 직접 import 하면 안 된다. 로케일 파일 로드는
// defineLocale() 이 전역 로케일을 바꾸는 부작용이 있고, 이 파일은 app.js 에서 **정적**
// import 라 앱 시작 시 무조건 평가된다. 마지막 줄이 'moment/locale/ru' 였던 탓에
// 앱 기본 로케일이 러시아어로 굳어, 설정이 English 인 사용자도 fromNow() 가
// '2 месяца назад' 로 표시됐다. import 는 config/momentLocale 한 곳에만 둔다.
import { syncMomentLocaleFromStorage } from '../../config/momentLocale';

import { AuthReducer } from '../../reducers/AuthReducer';

export const AvailableLang = LANG_SELECT_OPTIONS;

export const AvailableLangEmail = LANG_SELECT_OPTIONS;

// 폼 초기값 규칙 한 곳 — 첫 렌더와 서버 갱신(RefreshMySettings) 결과 반영이 같이 쓴다.
const buildInputs = (user) => ({
    rguardSettingLanguage: resolveUserLangCode(user),
    rguardSettingEmailReportLanguage: resolveStoredMailLangCode(user),
    // IMP-086: rguardSettingTimezone 제거 — dead field, WorkSchedule.timezone 로 일원화
    rguardSettingImgResolution: user?.rguardSettingImgResolution || "S",
    rguardSettingDarkMode: user?.rguardSettingDarkMode || false,
    rguardSettingDefaultShowMap: user?.rguardSettingDefaultShowMap,
    rguardSettingHomeErrorScope: user?.rguardSettingHomeErrorScope == undefined ? 1 : user.rguardSettingHomeErrorScope,
    rguardSettingHomeUpcomingScope: user?.rguardSettingHomeUpcomingScope || 7,
    rguardSettingHomeOverdueScope: user?.rguardSettingHomeOverdueScope == undefined ? 14 : user.rguardSettingHomeOverdueScope,
    rguardSettingHomeRecentInstallScope: user?.rguardSettingHomeRecentInstallScope || 7,
    rguardSettingDefaultFav: user?.rguardSettingDefaultFav || false,
    rguardSettingHideClosedIssue: user?.rguardSettingHideClosedIssue || false
});

// 관리자 레이아웃(/Admin/*)에서 보이는 항목. 나머지(기본 지도 보기·홈 표시 기간)는 기술자 홈 전용이라 숨긴다.
// 숨김이지 삭제가 아니다 — 저장 때 이 목록 밖 키는 페이로드에 싣지 않아 서버의 기존 값이 그대로 남는다.
export const ADMIN_VISIBLE_SETTING_KEYS = ['rguardSettingLanguage', 'rguardSettingEmailReportLanguage'];

export const buildSettingsPayload = (inputs, isAdmin) => {
    if (!isAdmin) return inputs;
    const payload = {};
    ADMIN_VISIBLE_SETTING_KEYS.forEach(k => { if (inputs?.[k] !== undefined) payload[k] = inputs[k]; });
    return payload;
};

export const ADMIN_DASHBOARD_SETTING_DEFAULTS = { dashboardtablelistcount: 30, dashboardshowprogval: 'N', dsservicecardduration: 30 };

const ADMIN_DASHBOARD_SETTING_KEYS = Object.keys(ADMIN_DASHBOARD_SETTING_DEFAULTS);

// 옛 화면이 숫자 칸을 글자 "30" 으로 저장한 기록이 있어 글자로 비교한다.
export const buildAdminSettingChanges = (savedSettings, adminInputs) => {
    const changes = [];
    ADMIN_DASHBOARD_SETTING_KEYS.forEach(k => {
        const value = adminInputs?.[k];
        if (value === undefined) return;
        if (String(value) === String(savedSettings?.[k] ?? ADMIN_DASHBOARD_SETTING_DEFAULTS[k])) return;
        if (k === 'dsservicecardduration') {
            if (!isValidWholeDays(value)) {
                console.warn('[UserSettings] buildAdminSettingChanges skipped', {
                    handler: 'UserSettings.buildAdminSettingChanges',
                    target: k,
                    value,
                    reason: 'not a whole number of days >= 1'
                });
                return;
            }
            changes.push({ name: k, value: Number(value) });
            return;
        }
        changes.push({ name: k, value });
    });
    return changes;
};

const buildAdminInputs = (user) => {
    const out = {};
    ADMIN_DASHBOARD_SETTING_KEYS.forEach(k => { out[k] = user?.settings?.[k] || ADMIN_DASHBOARD_SETTING_DEFAULTS[k]; });
    return out;
};

const UserSettings = ({ user, UpdateUser, updateUserSetting, isUpdateRes, ClearUpdateResult, RefreshMySettings, version }) => {

    const [i18n] = useTranslation();
    const isAdmin = isAdminPathname(window.location.pathname);
    const [submitted, setSubmitted] = useState(false);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState([]);

    const ImageRes = [
        { code: "O", title: "Original" },
        { code: "H", title: "High (1440x1080)" },
        { code: "S", title: "Standard (960x720)" }
    ]

    const [inputs, setInputs] = useState(() => buildInputs(user));
    // inputs 와 분리: 기술자 저장은 inputs 전체를 UpdateUser 로 보내므로 섞이면 사용자 문서 최상위에 키가 생긴다.
    const [adminInputs, setAdminInputs] = useState(() => buildAdminInputs(user));
    const showAdminDashboard = isAdmin && getLicenseRole(user) !== ROLE_EXTERNAL_DEALER;

    // 사용자가 칸을 한 번이라도 바꿨는지 / 언마운트 여부 — 서버 갱신 결과가 입력을 덮거나 죽은 화면을 건드리지 않게 한다.
    const touchedRef = useRef(false);
    const darkTouchedRef = useRef(false);
    const lk = { locked: !editing };
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        Promise.resolve(RefreshMySettings && RefreshMySettings()).then(fresh => {
            if (fresh && mountedRef.current && !touchedRef.current) {
                setInputs(buildInputs(fresh));
            }
        }).catch((e) => console.warn('[UserSettings] RefreshMySettings failed', String(e?.message || e).slice(0, 200)));
        return () => { mountedRef.current = false; };
    }, []);

    const errorDayOptions = [
        { code: 1, title: "1 " + t('common.label.day') },
        { code: 7, title: "1 " + t('common.label.week') },
        { code: 14, title: "2 " + t('common.label.weeks') },
        { code: 30, title: "1 " + t('common.label.month') },
        { code: 0, title: t('common.label.all') }
    ]

    const upComingScope = [
        { code: 7, title: "1 " + t('common.label.week') },
        { code: 14, title: "2 " + t('common.label.weeks') },
        { code: 30, title: "1 " + t('common.label.month') }
    ]

    const overDueScope = [
        { code: 7, title: "1 " + t('common.label.week') },
        { code: 14, title: "2 " + t('common.label.weeks') },
        { code: 30, title: "1 " + t('common.label.month') },
        { code: 60, title: "2 " + t('common.label.month') },
        { code: 90, title: "3 " + t('common.label.month') },
        { code: 0, title: t('common.label.all') }
    ]

    const recentInstallScope = [
        { code: 7, title: "1 " + t('common.label.week') },
        { code: 14, title: "2 " + t('common.label.weeks') },
        { code: 30, title: "1 " + t('common.label.month') },
    ]

    const form_controls = [
        // { title: "Theme", items : [
        //     { 
        //         type : "checkbox", 
        //         name : "rguardSettingDarkMode", 
        //         label : "Dark Mode", 
        //         val : inputs.rguardSettingDarkMode || false },
        // ] },
        {
            title: t('user.label.preferences'), items: [
                {
                    type: "select",
                    list: AvailableLang,
                    name: "rguardSettingLanguage",
                    label: userFieldLabel('rguardSettingLanguage'),
                    ...lk,
                    val: inputs.rguardSettingLanguage
                },
                {
                    type: "select",
                    list: AvailableLangEmail,
                    name: "rguardSettingEmailReportLanguage",
                    label: userFieldLabel('rguardSettingEmailReportLanguage'),
                    ...lk,
                    val: inputs.rguardSettingEmailReportLanguage
                },
                // {
                //     type : "select", 
                //     list : ImageRes,
                //     name : "rguardSettingImgResolution", 
                //     label : t('product.label.imageResolution'), 
                //     val : inputs.rguardSettingImgResolution },
                ...(isAdmin ? [] : [{
                    type: "checkbox",
                    name: "rguardSettingDefaultShowMap",
                    label: t('site.label.defaultMapView'),
                    ...lk,
                    val: inputs.rguardSettingDefaultShowMap
                }]),
                ...(showAdminDashboard ? [
                    {
                        type: "select2",
                        name: "dashboardtablelistcount",
                        label: t('dashboard.setting.tableListCount'),
                        ...lk,
                        val: adminInputs.dashboardtablelistcount,
                        list: [10, 20, 30, 40, 50, 100, 1000].map(n => ({ code: n, title: String(n) }))
                    },
                    {
                        type: "select2",
                        name: "dashboardshowprogval",
                        label: t('dashboard.setting.showValueInProgressBar'),
                        ...lk,
                        val: adminInputs.dashboardshowprogval,
                        list: [
                            { code: "Y", title: t('dashboard.setting.show') },
                            { code: "N", title: t('dashboard.setting.hide') }
                        ]
                    },
                    {
                        type: "number",
                        name: "dsservicecardduration",
                        label: t('dashboard.setting.cardDurationDays'),
                        ...lk,
                        val: adminInputs.dsservicecardduration,
                        min: 1,
                        step: 1
                    }
                ] : [])]
        },
        ...(isAdmin ? [] : [{
            title: t('dashboard.label.displayInterval'), items: [
                {
                    type: "select",
                    list: errorDayOptions,
                    name: "rguardSettingHomeErrorScope",
                    label: t('common.label.error'),
                    ...lk,
                    val: inputs.rguardSettingHomeErrorScope
                },
                {
                    type: "select",
                    list: overDueScope,
                    name: "rguardSettingHomeOverdueScope",
                    label: t('service.status.serviceOverdue'),
                    ...lk,
                    val: inputs.rguardSettingHomeOverdueScope
                },
                {
                    type: "select",
                    list: upComingScope,
                    name: "rguardSettingHomeUpcomingScope",
                    label: t('installation.label.schedule'),
                    ...lk,
                    val: inputs.rguardSettingHomeUpcomingScope
                },
                {
                    type: "select",
                    list: recentInstallScope,
                    name: "rguardSettingHomeRecentInstallScope",
                    label: t('product.status.newInstalled'),
                    ...lk,
                    val: inputs.rguardSettingHomeRecentInstallScope
                },
            ]
        }]),
        // { title: t('common.button.search'), items : [
        //     { 
        //         type : "checkbox", 
        //         name : "rguardSettingDefaultFav", 
        //         label : t('dashboard.label.favoriteView'), 
        //         val : inputs.rguardSettingDefaultFav || false },
        //     { 
        //         type : "checkbox", 
        //         css : "item nopadd bdT1",
        //         name : "rguardSettingHideClosedIssue", 
        //         label : t('common.label.hideCloseIssues'), 
        //         val : inputs.rguardSettingHideClosedIssue || false },
        // ] }
    ]

    useEffect(() => {
        if (isUpdateRes) {
            // i18n.changeLanguage(user.rguardSettingLanguage);
            // BUG-221: 19-case switch 가 app.js 와 똑같이 두 벌 있었다(CLAUDE.md §6).
            //
            // PR #559 R1 F-003 — 여기서 user.rguardSettingLanguage 를 직접 넘기면 안 된다.
            // 그 필드는 없을 수 있고(:77 의 `|| "eng"` 기본값이 그 상태를 증명한다),
            // undefined 를 넘기면 warn 없이 'en' 이 강제돼 language 기반 UI 언어와 갈라진다.
            // 이 effect 는 [user] deps 라 isUpdateRes 가 남아 있으면 설정 화면 마운트만으로도
            // 발화한다 — IubendaLogin.js:56 이 UpdateUser 후 ClearUpdateResult 를 부르지 않아
            // 실제로 잔존한다. 종전 switch 는 이 입력에 no-op 이었으므로 신규 실패 모드다.
            //
            // AuthAction.js:310 이 dispatch **직전에** localStorage 를 갱신하므로 저장 직후에도
            // 스토리지가 최신이다. 해석 SSoT(resolveCurrentLangCode: rguardSettingLanguage →
            // language → 로그인 전 선호도)를 그대로 타는 syncMomentLocaleFromStorage 를 쓴다.
            syncMomentLocaleFromStorage();
            ClearUpdateResult();
        }
    }, [user]);

    function handleChangeData(e) {
        const { name, value } = e.target;
        if (name == "rguardSettingDarkMode") {
            darkTouchedRef.current = true;
            if (value == true) {
                document.body.classList.add('dark-mode');
            } else {
                document.body.classList.remove('dark-mode');
            }
        }
        touchedRef.current = true;
        if (ADMIN_DASHBOARD_SETTING_KEYS.includes(name)) {
            setAdminInputs(prev => ({ ...prev, [name]: value }));
            return;
        }
        setInputs(inputs => ({ ...inputs, [name]: value }));
    }

    // 서버 값으로 되돌리기 — 입력·관리자 입력·다크모드 미리보기(바꾼 적이 있을 때만).
    const handleCancelClick = () => {
        setInputs(buildInputs(user));
        setAdminInputs(buildAdminInputs(user));
        touchedRef.current = false;
        if (darkTouchedRef.current) {
            document.body.classList.toggle('dark-mode', !!user?.rguardSettingDarkMode);
            darkTouchedRef.current = false;
        }
        setErrors([]);
        setEditing(false);
    }

    const handleUpdateClick = (e) => {
        e?.preventDefault?.();
        const errValidate = RayFormValidation(form_controls, inputs);
        if (errValidate.length > 0) {
            setErrors(errValidate);
        } else if (showAdminDashboard && !isValidWholeDays(adminInputs.dsservicecardduration)) {
            setErrors([{ name: 'dsservicecardduration', msg: t('dashboard.message.enterWholeDays') }]);
        } else {
            setSubmitted(true);
            setSaving(true);
            setErrors([]);
            const changes = showAdminDashboard ? buildAdminSettingChanges(user?.settings, adminInputs) : [];
            // 직렬 저장: 동시에 보내면 늦게 온 응답이 localStorage user 를 옛 값으로 덮는다.
            // 저장이 하나라도 실패하면 편집을 닫지 않아 저장 안 된 값이 저장된 것처럼 보이지 않게 한다.
            let allSaved = false;
            loadLang(inputs.rguardSettingLanguage)
                .then(() => UpdateUser(buildSettingsPayload(inputs, isAdmin)))
                .then(async (ok) => {
                    if (ok !== true) return;
                    for (const { name, value } of changes) {
                        if (await updateUserSetting(name, value) !== true) return;
                    }
                    allSaved = true;
                })
                .catch(e => console.warn('[UserSettings] save failed', String(e?.message || e).slice(0, 200)))
                .finally(() => {
                    if (!mountedRef.current) return;
                    setSaving(false);
                    if (allSaved) setEditing(false);
                });

            TranslateAction.SearchByLanguageDirect(inputs.rguardSettingLanguage).then(translate => {
                localStorage.setItem('translate', JSON.stringify(translate));
            });
        }
    }

    return (
        <div className="mb-2">
            <RayForm
                controls={form_controls}
                showAllFields={true}
                handleChangeData={handleChangeData}
                errors={errors}
                isForm={true}
            />
            <div className="text-muted p-2 text-center">App. Version : v{version}</div>
            <div className="section mb-2">
                <EditSaveCancelBar
                    block
                    editing={editing}
                    saving={saving}
                    onEdit={() => setEditing(true)}
                    onSave={handleUpdateClick}
                    onCancel={handleCancelClick} />
            </div>
        </div>
    )
}

const mapState = state => {
    const user = AuthAction.getUserInfo(state);
    const isUpdateRes = state.AuthReducer.isUpdateRes;
    const version = state.ProgramReducer.appVersion;
    return { user, isUpdateRes, version };
}

const mapDispatch = dispatch => ({
    UpdateUser: userInfo => dispatch(AuthAction.UpdateUser(userInfo)),
    updateUserSetting: (name, value) => dispatch(AuthAction.updateUserSetting(name, value)),
    ClearUpdateResult: () => dispatch(AuthAction.ClearUpdateResult()),
    RefreshMySettings: () => dispatch(AuthAction.RefreshMySettings())
})

export default connect(mapState, mapDispatch)(UserSettings);